import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

export const CloudCursor = () => {
  const { theme } = useTheme();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [trails, setTrails] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Don't add mouse listeners on mobile
    if (isMobile) return;
    
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsVisible(true);
      
      // Add trail
      const newTrail = {
        id: Date.now(),
        x: e.clientX,
        y: e.clientY,
      };
      
      setTrails(prev => [...prev.slice(-5), newTrail]);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isMobile]);

  // Clean up old trails
  useEffect(() => {
    if (isMobile) return;
    
    const interval = setInterval(() => {
      setTrails(prev => prev.slice(-5));
    }, 100);
    return () => clearInterval(interval);
  }, [isMobile]);

  // Don't render anything on mobile
  if (isMobile) return null;

  return (
    <>
      {/* Cloud trails */}
      <AnimatePresence>
        {trails.map((trail, index) => (
          <motion.div
            key={trail.id}
            className="fixed pointer-events-none z-[9999]"
            style={{
              left: trail.x - 10,
              top: trail.y - 10,
            }}
            initial={{ opacity: 0.6, scale: 0.5 }}
            animate={{ opacity: 0, scale: 1.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div 
              className="w-5 h-5 rounded-full"
              style={{
                background: theme === 'sunrise'
                  ? 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 70%)'
                  : 'radial-gradient(circle, rgba(45,212,191,0.5) 0%, rgba(45,212,191,0) 70%)',
                filter: 'blur(3px)',
              }}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Main cursor cloud */}
      <motion.div
        className="fixed pointer-events-none z-[9999]"
        animate={{
          x: mousePosition.x - 20,
          y: mousePosition.y - 15,
          opacity: isVisible ? 1 : 0,
        }}
        transition={{
          type: 'spring',
          damping: 30,
          stiffness: 200,
          mass: 0.5,
        }}
      >
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          {/* Cloud shape */}
          <div className="relative">
            {/* Main cloud body */}
            <div 
              className="w-10 h-6 rounded-full"
              style={{
                background: theme === 'sunrise'
                  ? 'radial-gradient(ellipse, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.4) 100%)'
                  : 'radial-gradient(ellipse, rgba(45,212,191,0.8) 0%, rgba(45,212,191,0.3) 100%)',
                boxShadow: theme === 'sunrise'
                  ? '0 4px 20px rgba(255,255,255,0.3)'
                  : '0 4px 20px rgba(45,212,191,0.3)',
                filter: 'blur(1px)',
              }}
            />
            {/* Cloud puff left */}
            <div 
              className="absolute -top-2 left-1 w-4 h-4 rounded-full"
              style={{
                background: theme === 'sunrise'
                  ? 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.2) 100%)'
                  : 'radial-gradient(circle, rgba(45,212,191,0.7) 0%, rgba(45,212,191,0.2) 100%)',
                filter: 'blur(1px)',
              }}
            />
            {/* Cloud puff right */}
            <div 
              className="absolute -top-1 right-2 w-3 h-3 rounded-full"
              style={{
                background: theme === 'sunrise'
                  ? 'radial-gradient(circle, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.2) 100%)'
                  : 'radial-gradient(circle, rgba(45,212,191,0.6) 0%, rgba(45,212,191,0.2) 100%)',
                filter: 'blur(1px)',
              }}
            />
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

export default CloudCursor;
